import axios from "axios"
import { useEffect, useRef, useState } from "react";
import { Input } from "../input";
import CustomerDetailModule from "./customerdetail.tsx";

export default function CallsModule({ baseUrl, client, currentUser, tab, setTab, setSelectedAgent }: { baseUrl: string, client: any, currentUser: any, tab: string, setTab: (tab: string) => void, setSelectedAgent: (agent: any) => void }) {
    const [loadingCalls, setLoadingCalls] = useState(false);
    const [calls, setCalls] = useState<any>([{}]);

    const [paging, setPaging] = useState(0);
    const CALLS_PER_PAGE = 10;
    const PAGE_BUTTONS_TO_SHOW = 5;
    const [totalCalls, setTotalCalls] = useState(0);
    const [sortBy, setSortBy] = useState("all");
    const [methodFilter, setMethodFilter] = useState("all");
    const [advisorFilter, setAdvisorFilter] = useState("all");
    const [search, setSearch] = useState("");


    const DEBOUNCE_DELAY = 500; // ms

    const abortControllerRef = useRef<AbortController | null>(null);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);

    const getLatestCalls = async (page: number) => {
        // Cancel previous request if exists
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            setLoadingCalls(true);
            const response = await axios.get(baseUrl + '/getAllCustomerCalls', {
                params:
                {
                    client_uid: client?.uid,
                    paging: page === 0 ? page : page * CALLS_PER_PAGE,
                    token: currentUser?.token,
                    length: CALLS_PER_PAGE,
                    sortBy: sortBy,
                    methodFilter: methodFilter,
                    agentFilter: advisorFilter,
                    search: search,
                }
            });

            if (response.data.status === "OK") {
                setCalls(response.data.data);
                setTotalCalls(response.data.totalCalls);
            }
        } catch (err) {
            // Ignore aborted requests
            if (axios.isCancel(err) || err?.name === "CanceledError") {
                return;
            }
            console.error(err);
        } finally {
            setLoadingCalls(false);
        }
    }

    useEffect(() => {
        if (!client) return;

        // Clear previous debounce
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            getLatestCalls(paging);
        }, DEBOUNCE_DELAY);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [
        client?.uid,
        baseUrl,
        currentUser,
        paging,
        sortBy,
        methodFilter,
        advisorFilter,
        search,
    ]);




    const totalPages = Math.ceil(totalCalls / CALLS_PER_PAGE);

    const goToPage = (page: number) => {
        if (page < 0 || page >= totalPages) return;
        setPaging(page);
    };

    // Calculate visible page buttons dynamically
    const getVisiblePages = () => {
        let start = Math.max(paging - Math.floor(PAGE_BUTTONS_TO_SHOW / 2), 0);
        let end = start + PAGE_BUTTONS_TO_SHOW;

        if (end > totalPages) {
            end = totalPages;
            start = Math.max(end - PAGE_BUTTONS_TO_SHOW, 0);
        }

        const pages = [];
        for (let i = start; i < end; i++) {
            pages.push(i);
        }
        return pages;
    };

    const [agents, setAgents] = useState([{}]);

    const getAgents = async () => {
        try {
            const response = await axios.get(baseUrl + '/getAllAgents', { params: { token: currentUser?.token, client_uid: client?.uid } });

            if (response.data.status === "OK") {
                setAgents(response.data.data);
            }
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        getAgents();
    }, [client, baseUrl])

    const [selectedId, setSelectedId] = useState<string | null>(null);

    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const handleCustomerNameClick = async (customer_id: string) => {
        try {
            setSelectedId(customer_id);
            const response = await axios.get(baseUrl + '/getCustomerById', { params: { token: currentUser?.token, customer_id: customer_id, client_uid: client?.uid } })

            if(response.data.status === "OK"){
                setSelectedCustomer(response.data.data);
            }
        } catch (err) {
            console.error(err);
        }
    }

    const handleNavigation = (to: string, agent: any) => {
        localStorage.setItem("previousPage", "Dashboard");
        setSelectedAgent(agent);
        setTab(to);
    }

    if (selectedCustomer && selectedId) {
        return (
            <CustomerDetailModule tab={tab} setTab={setTab} customer={selectedCustomer} setSelectedId={setSelectedId} baseUrl={baseUrl} currentUser={currentUser} client={client} />
        )
    }

    return (
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
                <h2 className="text-sm font-semibold">All kunder samtale</h2>
                <div className="flex flex-wrap gap-3 text-sm">
                    <div>
                        <label htmlFor="">Search</label>
                        <Input
                            type="text"
                            placeholder="Søk på navn, e-post, telefon eller UID..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full px-3 py-2 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-slate-500 mb-1">Betalingsmåte</label>
                        <select
                            value={methodFilter}
                            onChange={(e) => setMethodFilter(e.target.value)}
                            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm"
                        >
                            <option value="all">Alle</option>
                            <option value="Vipps">Vipps</option>
                            <option value="Stripe">Stripe</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs text-slate-500 mb-1">Veileder</label>
                        <select
                            value={advisorFilter}
                            onChange={(e) => setAdvisorFilter(e.target.value)}
                            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm"
                        >
                            <option value="all">Alle</option>
                            {agents.map((agent: any) => (
                                <option value={agent.uid}>{agent.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs text-slate-500 mb-1">Sorter</label>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm"
                        >
                            <option value="all">All</option>
                            <option value="missed">Missed</option>
                            <option value="completed">Completed</option>
                            <option value="marked">Mark for internal review</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-slate-500 border-b border-slate-200">
                                <th className="py-2 pr-4">Dato</th>
                                <th className="py-2 pr-4">Tid</th>
                                <th className="py-2 pr-4">Veileder</th>
                                <th className="py-2 pr-4">Kunde</th>
                                <th className="py-2 pr-4">Lengde</th>
                                <th className="py-2 pr-4">Pris</th>
                                <th className="py-2 pr-4">Betaling</th>
                                <th className="py-2 pr-4">Status</th>
                            </tr>
                        </thead>
                        <tbody className="text-slate-700">
                            {loadingCalls && (
                                <tr>
                                    <td colSpan={8}>
                                        <div className="flex items-center justify-center h-24">
                                            <div className="flex flex-col items-center">
                                                <div className="w-12 h-12 border-4 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                                                <p className="mt-3 text-gray-600 animate-pulse">Laster...</p>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {!loadingCalls && calls.map((call) => (
                                <tr key={call.id} className="border-b border-slate-100 hover:bg-slate-50">
                                    <td className="py-2 pr-4">{call.date}</td>
                                    <td className="py-2 pr-4">{call.time}</td>
                                    <td className="py-2 pr-4"><button type="button" className="underline" onClick={() => handleNavigation("Agent detail", call.agent)}>{call.agent_table_name}</button></td>
                                    <td className="py-2 pr-4"><button type="button" className="underline" onClick={() => handleCustomerNameClick(call.customer_table_id)}>{call.customer_table_name}</button></td>
                                    <td className="py-2 pr-4">{call.duration} min</td>
                                    <td className="py-2 pr-4">{call.charge_price?.toLocaleString("nb-NO")} NOK</td>
                                    <td className="py-2 pr-4">{call.payment_intent_id ? 'stripe' : 'vipps'}</td>
                                    <td className="py-2 pr-4">{call.call_status}</td>
                                </tr>
                            ))}
                            {!loadingCalls && calls.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="py-6 text-center text-sm text-slate-400">
                                        Ingen samtaler matcher filtrene.
                                    </td>
                                </tr>
                            )}
                            {/* PAGINATION */}
                            {totalPages > 1 && (
                                <tr>
                                    <td colSpan={8}>
                                        <div className="flex justify-center mt-4 gap-2 px-4 pt-4 pb-0">
                                            <button
                                                onClick={() => goToPage(paging - 1)}
                                                disabled={paging === 0}
                                                className="px-3 py-1 rounded border border-slate-300 bg-white disabled:opacity-50"
                                            >
                                                Prev
                                            </button>

                                            {getVisiblePages().map((i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => goToPage(i)}
                                                    className={`px-3 py-1 rounded border border-slate-300 ${paging === i ? "bg-indigo-500 text-white" : "bg-white"
                                                        }`}
                                                >
                                                    {i + 1}
                                                </button>
                                            ))}

                                            <button
                                                onClick={() => goToPage(paging + 1)}
                                                disabled={paging === totalPages - 1}
                                                className="px-3 py-1 rounded border border-slate-300 bg-white disabled:opacity-50"
                                            >
                                                Next
                                            </button>
                                        </div>

                                    </td>
                                </tr>
                            )}
                            {totalCalls > 0 && (
                                <tr>
                                    <td colSpan={8} className="text-center">
                                        <small className="text-gray-500"><em>Total no of records:</em> <b>{totalCalls}</b></small>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

            </div>



        </div>
    )
}